/**
 * Rate Limit Modal Component
 * Modal for 429 errors with countdown timer and Islamic guidance
 *
 * Accessibility:
 * - Focus trap within modal
 * - Keyboard dismissal with Escape
 * - Proper ARIA labeling
 * - High contrast countdown display
 * - Respects reduced motion preferences
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { motion } from 'motion/react';
import { X, Clock, Zap } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { getIslamicGuidance, getRateLimitTitle } from '../utils/islamicGuidance';
import type { RateLimitState } from '../hooks/useChatRateLimit';
import type { RateLimitType } from '../types/chat.types';
import { useFocusTrap } from '../../../../shared/hooks/useFocusTrap';
import { useReducedMotion } from '../../../../shared/hooks/useReducedMotion';

interface RateLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  rateLimitState: RateLimitState | null;
  errorMessage?: string;
  retryAfter?: number | null;
  /** Which rate limit was hit: 'burst' (short-term) or 'window' (sliding window) */
  limitType?: RateLimitType | null;
}

/**
 * Rate Limit Modal Component
 * Displays rate limit error with countdown and guidance
 */
export const RateLimitModal: React.FC<RateLimitModalProps> = ({
  isOpen,
  onClose,
  rateLimitState,
  errorMessage,
  retryAfter,
  limitType = 'burst', // Default to burst for backwards compatibility
}) => {
  const [countdown, setCountdown] = useState<number | null>(retryAfter ?? null);
  const { prefersReducedMotion, spring } = useReducedMotion();

  // Contextual messaging based on limit type
  const contextualContent = useMemo(() => {
    const isBurst = limitType === 'burst';

    return {
      // Icon and color theme
      icon: isBurst ? Zap : Clock,
      iconBgClass: isBurst
        ? 'bg-amber-100 dark:bg-amber-950/30'
        : 'bg-red-100 dark:bg-red-950/30',
      iconColorClass: isBurst
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-red-600 dark:text-red-400',

      // Countdown box styling
      countdownBgClass: isBurst
        ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-700'
        : 'bg-red-50 dark:bg-red-950/30 border-red-300 dark:border-red-700',
      countdownTextClass: isBurst
        ? 'text-amber-900 dark:text-amber-200'
        : 'text-red-900 dark:text-red-200',
      countdownValueClass: isBurst
        ? 'text-amber-800 dark:text-amber-300'
        : 'text-red-800 dark:text-red-300',
      countdownIconClass: isBurst
        ? 'text-amber-700 dark:text-amber-400'
        : 'text-red-700 dark:text-red-400',

      // Messages
      countdownLabel: isBurst
        ? 'Slow down! Too many messages too quickly.'
        : "You've reached your hourly message limit.",
      countdownSubLabel: isBurst
        ? 'Please wait before sending another message'
        : 'The limit will reset automatically',
    };
  }, [limitType]);

  // Focus trap for modal accessibility
  const modalRef = useFocusTrap<HTMLDivElement>({
    isActive: isOpen,
    autoFocus: true,
    restoreFocus: true,
    onEscape: onClose,
  });

  // Update countdown from rate limit state
  useEffect(() => {
    if (rateLimitState?.secondsUntilReset != null) {
      setCountdown(rateLimitState.secondsUntilReset);
    } else if (retryAfter != null) {
      setCountdown(retryAfter);
    }
  }, [rateLimitState, retryAfter]);

  // Countdown timer
  useEffect(() => {
    if (!isOpen || countdown === null || countdown <= 0) {
      return;
    }

    const interval = window.setInterval(() => {
      setCountdown(prev => {
        if (prev === null || prev <= 1) {
          setTimeout(onClose, 500);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(interval);
  }, [isOpen, countdown, onClose]);

  // Format countdown for display
  const formatCountdown = useCallback((seconds: number): string => {
    if (seconds <= 0) return '0s';

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return remainingSeconds > 0
        ? `${minutes}m ${remainingSeconds}s`
        : `${minutes}m`;
    }

    return `${remainingSeconds}s`;
  }, []);

  const guidance = getIslamicGuidance('rate_limit_reached', undefined, countdown ?? undefined);
  const title = getRateLimitTitle('rate_limit_reached');

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: prefersReducedMotion ? 0 : 0.15 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal container */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="presentation"
      >
        {/* Modal */}
        <motion.div
          ref={modalRef}
          initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.95, y: 20 }}
          animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
          transition={spring}
          className={cn(
            'bg-white dark:bg-gray-800 rounded-2xl',
            'shadow-xl border border-gray-200 dark:border-gray-700',
            'max-w-md w-full p-6',
            'max-h-[90vh] overflow-y-auto'
          )}
          onClick={(e) => e.stopPropagation()}
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="rate-limit-modal-title"
          aria-describedby="rate-limit-modal-description"
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-3">
              <div
                className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center shrink-0',
                  contextualContent.iconBgClass
                )}
                aria-hidden="true"
              >
                <contextualContent.icon className={cn('w-5 h-5', contextualContent.iconColorClass)} />
              </div>
              <div>
                <h2
                  id="rate-limit-modal-title"
                  className="text-lg font-semibold text-gray-900 dark:text-white"
                >
                  {title}
                </h2>
                {errorMessage && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {errorMessage}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className={cn(
                'p-2 rounded-lg min-w-[44px] min-h-[44px]',
                'flex items-center justify-center',
                'hover:bg-gray-100 dark:hover:bg-gray-700',
                'transition-colors shrink-0',
                'focus:outline-hidden focus:ring-2 focus:ring-gray-500 focus:ring-offset-2'
              )}
              aria-label="Close"
              type="button"
            >
              <X className="w-5 h-5 text-gray-500 dark:hover:text-gray-300" aria-hidden="true" />
            </button>
          </div>

          {/* Countdown - High contrast for accessibility */}
          {countdown !== null && countdown > 0 && (
            <div
              className={cn(
                'mb-4 p-4 rounded-xl border',
                contextualContent.countdownBgClass
              )}
              role="timer"
              aria-live="polite"
              aria-atomic="true"
            >
              <div className="flex items-center gap-3">
                <Clock className={cn('w-5 h-5 shrink-0', contextualContent.countdownIconClass)} aria-hidden="true" />
                <div className="flex-1">
                  <p className={cn('text-sm font-medium', contextualContent.countdownTextClass)}>
                    {contextualContent.countdownLabel}
                  </p>
                  <p className={cn('text-2xl font-bold mt-1 tabular-nums', contextualContent.countdownValueClass)}>
                    {formatCountdown(countdown)}
                  </p>
                  <p className={cn('text-xs mt-1', contextualContent.countdownTextClass, 'opacity-75')}>
                    {contextualContent.countdownSubLabel}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Islamic Guidance */}
          <div className="mb-4">
            <p
              id="rate-limit-modal-description"
              className="text-base text-gray-700 dark:text-gray-300 leading-relaxed"
            >
              {guidance}
            </p>
          </div>

          {/* Rate Limit Info */}
          {rateLimitState && (
            <div className="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
              <dl className="space-y-1 text-sm">
                <div className="flex items-center justify-between text-gray-600 dark:text-gray-400">
                  <dt>Message limit:</dt>
                  <dd className="font-medium tabular-nums text-gray-900 dark:text-gray-200">
                    {rateLimitState.limit}
                  </dd>
                </div>
                <div className="flex items-center justify-between text-gray-600 dark:text-gray-400">
                  <dt>Remaining:</dt>
                  <dd className="font-medium tabular-nums text-gray-900 dark:text-gray-200">
                    {rateLimitState.remaining}
                  </dd>
                </div>
                {rateLimitState.resetAt && (
                  <div className="flex items-center justify-between text-gray-600 dark:text-gray-400">
                    <dt>Resets at:</dt>
                    <dd className="font-medium tabular-nums text-gray-900 dark:text-gray-200">
                      {rateLimitState.resetAt.toLocaleTimeString()}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          )}

          {/* Action Button */}
          <div className="mt-6">
            <button
              onClick={onClose}
              className={cn(
                'w-full px-4 py-3 rounded-xl font-medium text-base',
                'min-h-[48px]', // Touch target
                'bg-emerald-600 hover:bg-emerald-700 text-white',
                'transition-colors duration-150',
                'shadow-md hover:shadow-lg',
                'focus:outline-hidden focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2'
              )}
              type="button"
            >
              I understand
            </button>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default RateLimitModal;
